import {
  Box, Button, Flex, Text
} from '@huspy/briks-web';
import Loader from '@shared/loader';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const DeleteCard = ({
  close,
  fileName,
  onDelete,
  isPendingDelete,
}: {
  onDelete(): void;
  fileName: string;
  isPendingDelete?: boolean;
  close: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Flex direction='column' gap='1'>
        <Text fontWeight='semibold'>
          {t('documents.bulkUpload.categorizeDocuments.deletionConfirmation')}
        </Text>
        <Text
          w='200px'
          fontWeight='semiBold'
          whiteSpace='nowrap'
          textOverflow='ellipsis'
          overflow='hidden'
          size='sm'
          color='neutral.500'
        >
          {fileName}
        </Text>
      </Flex>
      <Flex gap='2'>
        <Button
          px='4!'
          disabled={ isPendingDelete }
          loading={ isPendingDelete }
          variant={ isPendingDelete ? 'secondary' : 'primary' }
          onClick={ onDelete }
        >
          {t('documents.bulkUpload.categorizeDocuments.yesButton')}
        </Button>
        <Button px='4!' variant='outline' onClick={ close }>
          {t('documents.bulkUpload.categorizeDocuments.noButton')}
        </Button>
      </Flex>
    </>
  );
};

type Props = {
  children?: React.ReactNode;
  fileName: string;
  onDelete?(): void;
  onDownload?(): void;
  isPendingDownload?: boolean;
  isPendingDelete?: boolean;
};

const SingleDocumentCard = ({
  children, onDelete, fileName, onDownload, isPendingDownload, isPendingDelete,
}: Props) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  return (
    <Box
      bg='neutral.50'
      p='2'
      w='100%'
      borderRadius='2'
      border='1px solid'
      borderColor='neutral.100'
      mb='2'
    >
      <Flex gap='lg' align='center' justify='space-between' w='100%'>
        {isDeleting ? (
          <DeleteCard
            fileName={ fileName }
            onDelete={ onDelete! }
            isPendingDelete={ isPendingDelete }
            close={ () => setIsDeleting(false) }
          />
        ) : (
          <>
            <Flex direction='column' gap='1'>
              <Text
                w='300px'
                fontWeight='semiBold'
                whiteSpace='nowrap'
                textOverflow='ellipsis'
                overflow='hidden'
                size='lg'
              >
                {fileName}
              </Text>
              <Flex gap='1' align='center'>
                {onDownload
                  && (
                    <Text
                      size='sm'
                      cursor='pointer'
                      color='neutral.500'
                      onClick={ onDownload }
                    >
                      {isPendingDownload ? (
                        <Loader color='black' size={ 15 } />
                      ) : (
                        t('documents.bulkUpload.categorizeDocuments.preview')
                      )}
                    </Text>
                  )}
                {onDelete
                  && (
                    <>
                      <Text
                        size='xs'
                        cursor='pointer'
                        color='neutral.500'
                        fontWeight='bold'
                      >
                        |
                      </Text>
                      <Text
                        size='sm'
                        cursor='pointer'
                        color='neutral.500'
                        onClick={ () => setIsDeleting(true) }
                      >
                        {t('documents.bulkUpload.categorizeDocuments.delete')}
                      </Text>
                    </>
                  )}
              </Flex>
            </Flex>
            {children}
          </>
        )}
      </Flex>
    </Box>
  );
};

export default SingleDocumentCard;
